import { Button, Stack, TextField, Typography } from '@mui/material'
import { useSnackbar } from 'notistack'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { tryReadFromLocalStorage } from '@omnea-app/fe-browser-utils/tryReadFromLocalStorage'
import { tryRemoveFromLocalStorage } from '@omnea-app/fe-browser-utils/tryRemoveFromLocalStorage'
import { trySaveToLocalStorage } from '@omnea-app/fe-browser-utils/trySaveToLocalStorage'
import { LabeledCheckbox } from '@omnea-app/frontend/components/shared/labeled-checkbox/LabeledCheckbox'
import { isValidEmail } from '@omnea-app/packages/universal/general/isValidEmail'

const REMEMBERED_EMAIL_STORAGE_KEY = 'omnea_remembered_email'

export interface Props {
  email: string
  credentialsError: string | null
  onEmailChange: (val: string) => void
  onSubmit: () => Promise<void>
}

export const LoginEmailContainer = ({ email, credentialsError, onEmailChange, onSubmit }: Props) => {
  const { t } = useTranslation('login')
  const [typedEmail, setTypedEmail] = useState(email)
  const [emailError, setEmailError] = useState<null | string>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [rememberEmail, setRememberEmail] = useState(false)
  const { enqueueSnackbar } = useSnackbar()

  const validateEmail = () => {
    if (typedEmail === '') {
      setEmailError('validation.blank-email')
      return false
    }
    if (!isValidEmail(typedEmail)) {
      setEmailError('validation.invalid-email')
      return false
    }
    setEmailError(null)
    return true
  }

  useEffect(() => {
    // A URL/query email always takes precedence and is never treated as "remembered",
    // even when it arrives after mount (e.g. once the router hydrates a callback URL).
    if (email) {
      setTypedEmail(email)
      setRememberEmail(false)
    }
  }, [email])

  useEffect(() => {
    setEmailError(null)
  }, [typedEmail])

  useEffect(() => {
    // A URL/query email (the `email` prop) always takes precedence over a remembered one.
    if (email) return
    const rememberedEmail = tryReadFromLocalStorage({ key: REMEMBERED_EMAIL_STORAGE_KEY })
    if (rememberedEmail) {
      setTypedEmail(rememberedEmail)
      onEmailChange(rememberedEmail)
      setRememberEmail(true)
    }
    // Run once on mount to hydrate from localStorage without clobbering later prop changes.
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleSubmit = async () => {
    if (!validateEmail()) {
      return
    }
    if (rememberEmail) {
      trySaveToLocalStorage({ key: REMEMBERED_EMAIL_STORAGE_KEY, value: typedEmail })
    } else {
      tryRemoveFromLocalStorage({ key: REMEMBERED_EMAIL_STORAGE_KEY })
    }
    setIsSubmitting(true)
    try {
      await onSubmit()
    } catch (e) {
      enqueueSnackbar(t('email.error-submitting'), { variant: 'error' })
      console.error('error submitting login email request', { e })
    }
    setIsSubmitting(false)
  }

  const getErrorMessage = () => {
    if (emailError) return t(emailError)
    if (credentialsError) return credentialsError
    return ''
  }

  const handleEmailChanged = (newValue: string) => {
    setEmailError(null)
    setTypedEmail(newValue)
    onEmailChange(newValue)
  }

  return (
    <form
      noValidate
      onSubmit={(e) => {
        e.preventDefault()
        void handleSubmit()
      }}
    >
      <Stack spacing={0}>
        <Stack>
          <Stack spacing={4}>
            <Stack spacing={2}>
              <Typography suppressHydrationWarning variant="display-h2">
                {t('email.title')}
              </Typography>
              <Typography suppressHydrationWarning variant="body-regular" color="text.muted">
                {t('email.subtitle')}
              </Typography>
            </Stack>

            <Stack spacing={2}>
              <TextField
                fullWidth
                type="email"
                name="email"
                label={t('email-label')}
                value={typedEmail}
                suppressHydrationWarning
                onChange={(e) => {
                  handleEmailChanged(e.target.value)
                }}
                error={Boolean(credentialsError) || Boolean(emailError)}
                onBlur={validateEmail}
                slotProps={{
                  htmlInput: {
                    'data-testid': 'input-email',
                  },
                }}
                helperText={getErrorMessage()}
              />
              <LabeledCheckbox
                label={t('remember-email')}
                checked={rememberEmail}
                onChange={setRememberEmail}
                data-testid="checkbox-remember-email"
              />
            </Stack>

            <Button
              loading={isSubmitting}
              size="medium"
              data-testid="btn-login"
              type="submit"
              color="primary"
              variant="contained"
              fullWidth
              suppressHydrationWarning
            >
              {t('login-button')}
            </Button>
          </Stack>
        </Stack>
      </Stack>
    </form>
  )
}
